import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import DescriptionIcon from '@mui/icons-material/Description';
import { usePersistedDataStore } from '../../../store.js';
import { convertMVToAV } from '../../../../utils/conversionUtils.js';
import ValueCell from './ValueCell.js';
import { findWorkupFile, isWorkupPdf } from '../../Workups/WorkupVisualization/workupFileHelpers.js';
import { isWorkupAboveIfmv } from '../utils/helpers.js';
import { blue } from '@mui/material/colors';

const workupCellHasPositiveValue = (raw) => {
  if (raw == null || raw === '') return false;
  if (typeof raw === 'number') return Number.isFinite(raw) && raw > 0;
  const n = parseFloat(String(raw).replace(/[^-\d.]/g, ''));
  return Number.isFinite(n) && n > 0;
};

/**
 * WorkupCell component that subscribes to store for per-parcel loading state
 * This allows individual cells to update their loading state without rememoizing columns
 * The loading spinner is centered within the cell
 * Includes document icon button for viewing workup visualization
 */
function WorkupCell(props) {
  const { row, avToggleActive, onFileView, reportType, townView = false } = props;

  // Subscribe to store for this specific parcel's loading state
  const workupRegenerating = usePersistedDataStore(
    (state) => state.workupRegeneratingByParcel[row.parcel_id] ?? false
  );

  const files = Array.isArray(row.S3Files) ? row.S3Files : [];
  const nyscefFiles = Array.isArray(row.NyscefS3Files) ? row.NyscefS3Files : [];
  const hasNyscef = nyscefFiles.some((f) => f?.Url);

  // Determine workup type based on reportType
  // 'standard' -> 'sales', 'assessment' -> 'assessment' (or 'equity')
  const workupType = useMemo(() => {
    if (reportType === 'assessment') {
      return 'assessment';
    }
    return 'sales';
  }, [reportType]);

  // Any generated workup (FNMA, one-pager, nomaps, equity, village) enables the docs icon
  const workupFile = useMemo(() => {
    return findWorkupFile(files, workupType) || files.find((f) => isWorkupPdf(f) && (f.Url || f.url)) || null;
  }, [files, workupType]);

  const hasFile = !!workupFile;
  const canOpenDocs = hasFile || hasNyscef;
  const showEvidenceIconButton =
    (!townView || canOpenDocs) && !(townView && (row._isSettled || row.StipFinalized));

  // If loading, show centered spinner (string set by createRows while comp workup pending)
  if (workupRegenerating || row.loading || row.ProprietyWorkup === 'Loading...') {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100%',
        }}
      >
        <CircularProgress size={16} />
      </Box>
    );
  }

  if (row._townWorkupNotAvailable || (townView && isWorkupAboveIfmv(row))) {
    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        height: '100%',
        width: '100%',
        position: 'relative',
      }}
      >
        <span style={{ color: '#888', fontStyle: 'italic' }}>Not Available</span>
      </Box>
    );
  }

  let displayValue = row.ProprietyWorkup;
  if (avToggleActive && workupCellHasPositiveValue(row.ProprietyWorkup)) {
    displayValue = convertMVToAV(row.ProprietyWorkup, row.EQ, row.RAR);
  }

  const showNoneAvailable =
    !workupCellHasPositiveValue(row.ProprietyWorkup) ||
    !workupCellHasPositiveValue(displayValue);

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'row',
      alignItems: 'center',
      height: '100%',
      width: '100%',
      position: 'relative',
    }}>
      {showNoneAvailable ? (
        <span style={{ color: '#888', fontStyle: 'italic' }}>None Available</span>
      ) : (
      <ValueCell 
        value={displayValue} 
        loading={false} 
        type={avToggleActive ? "av" : "market"} 
        impliedReduction={townView ? null : row.impliedReduction}
        decimalPlaces={1}
      />
      )}
      {showEvidenceIconButton && (
      <Box sx={{
        position: 'absolute',
        right: -8,
        top: '50%',
        transform: 'translateY(-50%)',
        display: 'flex',
        alignItems: 'center',
      }}>
        <Tooltip 
          title={canOpenDocs ? 'View Evidence' : 'No Workup'}
          disableInteractive
          slotProps={{
            tooltip: {
              sx: {
                marginTop: '4px !important'
              }
            }
          }}
        >
          <span>
            <IconButton
              size="small"
              disabled={!canOpenDocs}
              {...(townView ? { 'data-town-tutorial': 'workup-doc-icon' } : {})}
              onClick={() => {
                if (canOpenDocs && onFileView) {
                  onFileView(workupFile || null, workupType, files, nyscefFiles, row);
                }
              }}
              sx={{
                backgroundColor: 'transparent !important',
                color: 'text.secondary',
                visibility: 'visible !important',
                display: 'flex !important',
                border: 'none !important',
                '&:hover': {
                  backgroundColor: 'transparent !important',
                  color: blue[700],
                },
                '&.Mui-disabled': {
                  color: '#bdbdbd !important',
                  visibility: 'visible !important',
                  backgroundColor: 'transparent !important',
                  display: 'flex !important',
                  pointerEvents: 'auto',
                },
              }}
            >
              <DescriptionIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      )}
    </Box>
  );
}

export default React.memo(WorkupCell);

